import { Component, DestroyRef, OnInit, signal, inject, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { EMPTY, Subject, catchError, forkJoin, merge, switchMap, timer } from 'rxjs';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { RegistrationStep } from '../../../core/services/registration-step.service';
import { Alert, AlertStatus, getAlertStatus } from '../../../core/models/models';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ColumnConfig } from '../dashboard/table-config';

@Component({
  selector: 'app-alerts-registry',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    ToastModule,
    BreadcrumbModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    RegistryTableComponent,
  ],
  templateUrl: './alerts-registry.component.html',
  styleUrl: './alerts-registry.component.scss',
})
export class AlertsRegistryComponent implements OnInit {
  private professionalsService = inject(ProfessionalsService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private title = inject(Title);
  private readonly destroyRef = inject(DestroyRef);
  private readonly refreshAlerts$ = new Subject<void>();

  alerts = signal<Alert[]>([]);
  loading = signal(false);

  readonly breadcrumbItems = [
    { label: 'Avvisi' },
  ];

  readonly columns: ColumnConfig[] = [
    { title: 'ID Fiduciario',  field: 'professionalId',   sortable: true,  filterType: 'text',   filterPlaceholder: 'Cerca ID' },
    { title: 'Professionista', field: 'professionalName', sortable: true,  filterType: 'text',   filterPlaceholder: 'Cerca nome' },
    { title: 'Questionario',   field: 'questionnaireName',sortable: true,  filterType: 'text',   filterPlaceholder: 'Cerca questionario' },
    { title: 'Domanda',        field: 'questionText',     type: 'truncated', truncateAt: 60, filterType: 'text', filterPlaceholder: 'Cerca domanda' },
    { title: 'Risposta',       field: 'optionText',       type: 'truncated', truncateAt: 60, filterType: 'text', filterPlaceholder: 'Cerca risposta' },
    { title: 'Data',           field: 'triggeredAt',      sortable: true,  filterType: 'text',   filterPlaceholder: 'Filtra data (gg/mm/aaaa)' },
    { title: 'Stato',          field: 'alertStatus',      type: 'tag',     filterType: 'select', filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Non letto',  value: 'UNREAD' },
        { label: 'In carico',  value: 'IN_PROGRESS' },
        { label: 'Risolto',    value: 'RESOLVED' },
      ],
    },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  /** Dati arricchiti con campo alertStatus per il filtro/tag */
  tableData = computed(() =>
    this.alerts().map(a => {
      const truncate = (text: string | null | undefined, max: number) => {
        if (!text) return '';
        return text.length > max ? text.slice(0, max) + '…' : text;
      };
      const questionFull  = a.questionText  ?? '';
      const optionFull    = a.optionText     ?? a.alertMessage ?? '';
      return {
        ...a,
        alertStatus:        getAlertStatus(a),
        professionalName:   a.professionalName ?? a.professionalId,
        questionText:       truncate(questionFull, 60),
        questionText_full:  questionFull.length > 60 ? questionFull : null,
        optionText:         truncate(optionFull, 60),
        optionText_full:    optionFull.length > 60 ? optionFull : null,
        triggeredAt: a.triggeredAt
          ? new Date(a.triggeredAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' })
          : '—',
      };
    })
  );

  ngOnInit(): void {
    this.title.setTitle('Avvisi | PalEasy');
    const ELIGIBLE_STEPS = [
      RegistrationStep.VERIFICATION,
      RegistrationStep.MFA_SETUP,
      RegistrationStep.CONTRACT_SENT,
      RegistrationStep.CONTRACT_SIGNATURE,
      RegistrationStep.CONTRACT_COUNTERSIGNATURE,
      RegistrationStep.CONTRACT_ACTIVATED,
    ];

    merge(timer(0, 120_000), this.refreshAlerts$, this.professionalsService.alertsChanged$).pipe(
      switchMap(() => {
        this.loading.set(true);
        return forkJoin({
          alerts: this.professionalsService.getAlerts(true, true),
          professionals: this.professionalsService.getProfessionals(0, 9999, undefined, { step: ELIGIBLE_STEPS }, true),
        }).pipe(
          catchError(() => {
            this.loading.set(false);
            this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Impossibile caricare gli avvisi' });
            return EMPTY;
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: ({ alerts, professionals }) => {
        const eligibleIds = new Set(
          (professionals?.data?.content ?? []).map((p: any) => p.id as string)
        );
        this.alerts.set(alerts.filter(alert => eligibleIds.has(alert.professionalId)));
        this.loading.set(false);
      },
    });
  }

  loadAlerts(): void {
    this.refreshAlerts$.next();
  }

  getStatus(alert: Alert): AlertStatus {
    return getAlertStatus(alert);
  }

  getStatusLabel(status: AlertStatus): string {
    const map: Record<AlertStatus, string> = { UNREAD: 'Non letto', IN_PROGRESS: 'In carico', RESOLVED: 'Risolto' };
    return map[status];
  }

  getStatusSeverity(status: AlertStatus): 'danger' | 'warn' | 'success' {
    const map: Record<AlertStatus, 'danger' | 'warn' | 'success'> = { UNREAD: 'danger', IN_PROGRESS: 'warn', RESOLVED: 'success' };
    return map[status];
  }

  takeInCharge(alert: Alert): void {
    this.professionalsService.takeInChargeAlert(alert.id).subscribe({
      next: () => {
        this.alerts.update((list) => list.map((a) => (a.id === alert.id ? { ...a, disabled: true, resolved: false } : a)));
        this.messageService.add({ severity: 'success', summary: 'Successo', detail: 'Avviso preso in carico' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Operazione fallita' }),
    });
  }

  resolve(alert: Alert): void {
    this.professionalsService.resolveAlert(alert.id).subscribe({
      next: () => {
        this.alerts.update((list) => list.map((a) => (a.id === alert.id ? { ...a, resolved: true, disabled: true } : a)));
        this.messageService.add({ severity: 'success', summary: 'Successo', detail: 'Avviso risolto' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Operazione fallita' }),
    });
  }

  goToQuestionnaire(alert: Alert): void {
    this.router.navigate(['/axa/review-professional', alert.professionalId]);
  }
}
